import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { AUTENTICATE_API } from "../../services/apiCalls/authApi";
import "./employee_form.css";
import Breadcrumbs from "../layout/breadcrumb";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const EmployeeForm = ({ onSubmit }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const empId = searchParams.get("emp_id");
  const view = searchParams.get("action");

  const [names, setNames] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [roles, setRoles] = useState([]); // State for roles
  const [formData, setFormData] = useState({
    name: "",
    designation_type: "",
    emails: [""],
    phoneNumbers: [""],
    role_id: "",
    status: "",
  });

  useEffect(() => {
    const fetchEmployeeDetails = async () => {
      if (empId) {
        try {
          const employeeResponse = await AUTENTICATE_API.getEmployeeDetails(
            empId
          );
          if (employeeResponse && employeeResponse.status === 200) {
            const employeeData = employeeResponse.employee_role[0];
            setFormData({
              name: employeeData.name || "",
              designation_type: employeeData.designation_type || "",
              emails: employeeData.email ? employeeData.email.split(",") : [""],
              phoneNumbers: employeeData.contact_number
                ? employeeData.contact_number.split(",")
                : [""],
              role_id: employeeData.role_id || "",
              status: employeeData.status || "",
            });
          } else {
            toast.error("Failed to fetch employee details.");
          }
        } catch (error) {
          toast.error("An error occurred while fetching employee details.");
        }
      }
    };

    fetchEmployeeDetails();
  }, [empId]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const fetchedNamesResponse = await AUTENTICATE_API.getEmployeeNames();
        const fetchedDesignationsResponse =
          await AUTENTICATE_API.getEmployeeDesignations();
        const fetchedRolesResponse = await AUTENTICATE_API.getRoles();

        const fetchedNames =
          fetchedNamesResponse?.Name?.map((item) => ({
            id: item.employee_id,
            name: item.employee_name,
          })) || [];
        const fetchedDesignations =
          fetchedDesignationsResponse?.Designation?.map((item) => ({
            id: item.des_id,
            name: item.designation,
          })) || [];
        const fetchedRoles =
          fetchedRolesResponse?.roles?.map((role) => ({
            id: role.role_id,
            name: role.role_name,
          })) || [];

        setNames(fetchedNames);
        setDesignations(fetchedDesignations);
        setRoles(fetchedRoles);
      } catch (error) {
        toast.error("Failed to fetch dropdown options.");
      }
    };

    fetchOptions();
  }, []);

  const handleChange = (field, value, index = null) => {
    if (index !== null) {
      setFormData((prevData) => {
        const updatedArray = [...prevData[field]];
        updatedArray[index] = value;
        return { ...prevData, [field]: updatedArray };
      });
    } else {
      setFormData((prevData) => ({ ...prevData, [field]: value }));
    }
  };

  const handleAddField = (field) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: [...prevData[field], ""],
    }));
  };

  const handleRemoveField = (field, index) => {
    setFormData((prevData) => {
      const updatedArray = prevData[field].filter((_, i) => i !== index);
      return { ...prevData, [field]: updatedArray };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check for duplicate emails
    const emailSet = new Set(formData.emails);
    if (emailSet.size !== formData.emails.length) {
      toast.error("Duplicate email addresses are not allowed.");
      return;
    }

    // Check for duplicate phone numbers
    const phoneSet = new Set(formData.phoneNumbers);
    if (phoneSet.size !== formData.phoneNumbers.length) {
      toast.error("Duplicate phone numbers are not allowed.");
      return;
    }

    // Transform formData into the required payload format
    const payload = {
      name: formData.name,
      designation_type: formData.designation_type,
      email: formData.emails.join(","),
      contact_number: formData.phoneNumbers.join(","),
      role_id: formData.role_id,
      status: formData.status,
    };

    try {
      if (empId) {
        payload.emp_id = empId;
        const response = await AUTENTICATE_API.updateEmployee(payload);
        if (response && response.status === 200) {
          toast.success("Employee updated successfully!");
          navigate("/employees");
        } else {
          toast.error(response.error);
        }
      } else {
        const response = await AUTENTICATE_API.createEmployee(payload);
        if (response && response.status === 201) {
          toast.success("Employee created successfully!");
          navigate("/employees");
        } else {
          toast.error(response.error);
        }
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to submit the form. Please try again.");
    }
  };

  return (
    <>
      <Breadcrumbs
        title="Employees"
        menu="Employees"
        link={empId ? "Edit Employee" : "Create Employee"}
      />
      <form onSubmit={handleSubmit} className="employee-form">
        <div className="form-field">
          <label>Name:</label>
          <select
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            disabled={view}
          >
            <option value="">Select Name</option>
            {names.map(({ id, name }) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label>Designation:</label>
          <select
            value={formData.designation_type}
            onChange={(e) => handleChange("designation_type", e.target.value)}
            disabled={view}
          >
            <option value="">Select Designation</option>
            {designations.map(({ id, name }) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label>Role:</label>
          <select
            value={formData.role_id}
            onChange={(e) => handleChange("role_id", e.target.value)}
            disabled={view}
          >
            <option value="">Select Role</option>
            {roles.map(({ id, name }) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label>Status:</label>
          <select
            value={formData.status}
            onChange={(e) => handleChange("status", e.target.value)}
            disabled={view}
          >
            <option value="">Select Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>

        <div className="form-field">
          <label>Emails:</label>
          {formData.emails.map((email, index) => (
            <div key={index} className="sub-field">
              <input
                type="email"
                value={email}
                onChange={(e) => handleChange("emails", e.target.value, index)}
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                required
                disabled={view}
              />
              {index === 0 ? (
                <button
                  type="button"
                  onClick={() => handleAddField("emails")}
                  className="add-btn"
                  disabled={view}
                >
                  +
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleRemoveField("emails", index)}
                  className="remove-btn"
                  disabled={view}
                >
                  -
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="form-field">
          <label>Phone Numbers:</label>
          {formData.phoneNumbers.map((phone, index) => (
            <div key={index} className="sub-field">
              <input
                type="tel"
                value={phone}
                onChange={(e) =>
                  handleChange("phoneNumbers", e.target.value, index)
                }
                pattern="[0-9]{10}"
                required
                disabled={view}
              />
              {index === 0 ? (
                <button
                  type="button"
                  onClick={() => handleAddField("phoneNumbers")}
                  className="add-btn"
                  disabled={view}
                >
                  +
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleRemoveField("phoneNumbers", index)}
                  className="remove-btn"
                  disabled={view}
                >
                  -
                </button>
              )}
            </div>
          ))}
        </div>

        {!view && (
          <button type="submit" className="submit-btn">
            {empId ? "Update Employee" : "Create Employee"}
          </button>
        )}
      </form>
    </>
  );
};

export default EmployeeForm;
